import sanitizeHtml, { type TransformTag } from 'sanitize-html';
import { isYouTubeVideoId } from '@/lib/youtubeEmbed';

/**
 * 動画プレースホルダの div かどうかを判定し、そうでなければ捨てさせる
 *
 * 目的:
 * - 本文HTMLに残してよい div を「動画のプレースホルダ1種類」に限る
 *
 * 引数:
 * - tagName: 'div'
 * - attribs: その div が持っている属性
 *
 * 戻り値:
 * - sanitize-html の transformTags が受け取る { tagName, attribs }
 *
 * 仕様:
 * - data-youtube-id が11文字の動画IDなら、その属性だけ残して通す
 * - そうでない div は、許可リストに無いタグ名へ付け替えてタグだけ解かせる。
 *   要素ごと削除すると、利用者が書いた <div><p>本文</p></div> の本文まで消える
 * - 本文に <iframe> は入れない。埋め込み枠は描画のときに renderYouTubeEmbeds が
 *   このプレースホルダから組み立てる
 * - この判定はバックエンドの MemberPostHtml::isAllowedVideoId と揃えてある
 */
const transformBodyDiv: TransformTag = (tagName, attribs) => {
  const videoId = attribs['data-youtube-id'] ?? '';
  if (!isYouTubeVideoId(videoId)) {
    return { tagName: 'x-unwrap-div', attribs: {} };
  }
  return { tagName, attribs: { 'data-youtube-id': videoId } };
};

/**
 * HTMLを安全にサニタイズする
 *
 * 目的:
 * - 管理画面で入力したHTML（TipTap出力）を公開側で安全に表示する（XSS対策）
 *
 * 引数:
 * - html: サニタイズ対象のHTML文字列
 *
 * 戻り値:
 * - サニタイズ済みHTML文字列
 *
 * 想定される例外:
 * - sanitize-html内部で例外が出る可能性は低いが、呼び出し側でtry-catchしてもよい
 */
export function sanitizeHtmlContent(html: string): string {
  const input = html ?? '';
  return sanitizeHtml(input, {
    allowedTags: [
      // テキスト構造
      'p',
      'br',
      'hr',
      'blockquote',
      // 見出し
      'h1',
      'h2',
      'h3',
      'h4',
      // 強調
      'strong',
      'em',
      'u',
      's',
      // リスト
      'ul',
      'ol',
      'li',
      // リンク/画像
      'a',
      'img',
      // コード
      'code',
      'pre',
      // 互換用
      'span',
      // div は「動画のプレースホルダ」専用で通す。許可する属性は data-youtube-id だけで、
      // それ以外の div は transformTags でタグを解く（バックエンドの
      // App\Support\MemberPostHtml::ALLOWED_TAGS と必ず一致させる）
      'div',
    ],
    allowedAttributes: {
      a: ['href', 'title', 'target', 'rel'],
      img: ['src', 'alt', 'title', 'width', 'height', 'loading'],
      div: ['data-youtube-id'],
      code: ['class'],
      span: ['class'],
      p: ['class'],
      h1: ['class'],
      h2: ['class'],
      h3: ['class'],
      h4: ['class'],
      ul: ['class'],
      ol: ['class'],
      li: ['class'],
      blockquote: ['class'],
      pre: ['class'],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: {
      img: ['http', 'https'],
    },
    // 外部リンクの安全対策（target=_blankのとき）
    transformTags: {
      a: (tagName, attribs) => {
        const href = attribs.href ?? '';
        const isExternal = /^https?:\/\//i.test(href);
        if (isExternal) {
          return {
            tagName,
            attribs: {
              ...attribs,
              target: '_blank',
              rel: 'noopener noreferrer',
            },
          };
        }
        return { tagName, attribs };
      },
      img: (tagName, attribs) => {
        // 画像はlazyを付ける
        return {
          tagName,
          attribs: {
            ...attribs,
            loading: attribs.loading ?? 'lazy',
          },
        };
      },
      div: transformBodyDiv,
    },
  });
}



/**
 * 会員プロフィール本文で許可する画像ホストか判定する
 *
 * 目的:
 * - 会員が本文に差し込める画像を、結会が配信しているものだけに限る
 *
 * 引数:
 * - src: img の src 属性値
 *
 * 戻り値:
 * - boolean（true なら表示してよい）
 *
 * 仕様:
 * - 相対パス（/media/... など）は自サイト配信なので許可する
 * - CloudFront（本番の配信元）と localhost（開発）を許可する
 * - それ以外の外部ドメインは弾く。他所のサーバに無断で負荷をかけないためと、
 *   そのサイトが消えたときに会員のプロフィールがリンク切れになるのを防ぐため
 *
 * セキュリティ上の注意:
 * - バックスラッシュ・制御文字（タブ・改行・復帰など）・エンコードされたバックスラッシュ
 *   （%5c）を含む値は、相対パス判定に入る前に弾く。ブラウザ（WHATWG URL）は "\" を "/" と
 *   同じ区切りとして扱い、かつ URL の解析前にタブ・LF・CR を取り除くため、
 *   "/\evil.example.com/a.png" や "/\t/evil.example.com/a.png" のような値は
 *   ブラウザ上では "//evil.example.com/a.png"（プロトコル相対 URL = 別オリジン）に解決される。
 * - ASCII 以外の文字を含む値も弾く。全角英数字やアラビア数字の句点（U+3002 など）は
 *   ブラウザの URL 正規化（IDNA/UTS46）で ASCII に丸められ、"ｄ123.cloudfront.net" や
 *   "evil.example.com。cloudfront.net" が見た目どおりの別ホストとして解決されることがある
 *   （PHP 側の parse_url はこの丸め込みをしないため、正規化前の生バイトで判定する必要がある）
 * - ホストは英数字・ドット・ハイフンだけで構成され、先頭/末尾がドットやハイフンでなく、
 *   連続ドットも無いことを要求する。"attacker.com;.cloudfront.net" や ".cloudfront.net"
 *   のような、サフィックス判定だけを通す偽ホストを弾くため
 * - 以上はバックエンドの MemberPostHtml::isAllowedImageSrc() と同じ判定に揃えてある
 */
function isAllowedProfileImageSrc(src: string): boolean {
  const value = (src ?? '').trim();
  if (value === '') return false;

  if (/[\\\x00-\x1f\x7f-\uffff]/.test(value) || value.toLowerCase().includes('%5c')) return false;

  // 相対パスは自サイト配信
  if (value.startsWith('/') && !value.startsWith('//')) return true;

  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;

    const host = url.hostname.toLowerCase();
    if (
      !/^[a-z0-9.-]+$/.test(host) ||
      host.startsWith('.') ||
      host.startsWith('-') ||
      host.endsWith('.') ||
      host.endsWith('-') ||
      host.includes('..')
    ) {
      return false;
    }

    return (
      host === 'cloudfront.net' ||
      host.endsWith('.cloudfront.net') ||
      host === 'localhost' ||
      host === '127.0.0.1'
    );
  } catch {
    // URL として解釈できないものは通さない
    return false;
  }
}

/**
 * 会員プロフィール本文のHTMLをサニタイズする
 *
 * 目的:
 * - 会員自身が書いた本文HTML（TipTap出力）を公開ページで安全に表示する
 *
 * 引数:
 * - html: サニタイズ対象のHTML文字列
 *
 * 戻り値:
 * - サニタイズ済みHTML文字列
 *
 * 仕様:
 * - 管理画面のブログ用（sanitizeHtmlContent）とは別ポリシー。会員に開放するのは
 *   太字・リンク・箇条書き・画像の4つだけで、見出し/引用/コードは許可しない。
 *   ページ側の見出しと入れ子になって階層が壊れるため
 * - 画像は結会が配信しているホストのものだけ通す（isAllowedProfileImageSrc）
 */
export function sanitizeMemberProfileHtml(html: string): string {
  const input = html ?? '';
  return sanitizeHtml(input, {
    allowedTags: [
      'p',
      'br',
      'strong',
      'em',
      'ul',
      'ol',
      'li',
      'a',
      'img',
      // div は「動画のプレースホルダ」専用で通す。許可する属性は data-youtube-id だけで、
      // それ以外の div は transformTags でタグを解く（バックエンドの
      // App\Support\MemberPostHtml::ALLOWED_TAGS と必ず一致させる）
      'div',
    ],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
      // height は許可しない。写真の大きさは width だけで決める方針で（会員ブログと同じ）、
      // 両方が残ると縦横比の崩れた指定を表示してしまう
      img: ['src', 'alt', 'loading', 'width'],
      div: ['data-youtube-id'],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedSchemesByTag: {
      img: ['http', 'https'],
    },
    // プロトコル相対URL（"//evil.example.com/x" 等）の href を許さない。
    // バックエンド（MemberPostHtml::cleanAttributes）は相対パスでも http/https/mailto
    // でもない href を丸ごと削除するため、既定の true のままだと href が残ってしまい、
    // rel="noopener noreferrer" も付かない別オリジンリンクが漏れる
    allowProtocolRelative: false,
    transformTags: {
      a: (tagName, attribs) => {
        const href = attribs.href ?? '';
        const isExternal = /^https?:\/\//i.test(href);
        if (isExternal) {
          return {
            tagName,
            attribs: { ...attribs, target: '_blank', rel: 'noopener noreferrer' },
          };
        }
        return { tagName, attribs };
      },
      img: (tagName, attribs) => {
        if (!isAllowedProfileImageSrc(attribs.src ?? '')) {
          // src だけ落とすと <img> が残って壊れた画像アイコンが出る。
          // 許可リストに無いタグ名へ変えることで、タグごと捨てさせる
          // （img は中身を持たないので、跡形なく消える）
          return { tagName: 'disallowed-image', attribs: {} };
        }
        return {
          tagName,
          attribs: { ...attribs, loading: attribs.loading ?? 'lazy' },
        };
      },
      div: transformBodyDiv,
    },
  });
}

/**
 * 会員が書いたブログ本文のHTMLをサニタイズする
 *
 * 仕様:
 * - バックエンドの App\Support\MemberPostHtml::ALLOWED_TAGS と必ず一致させる
 * - h1 と pre/code は許可しない（ページ側の見出しと入れ子になって階層が壊れるため）
 * - 画像は結会が配信しているホストのものだけ通す（isAllowedProfileImageSrc）
 */
export function sanitizeMemberPostHtml(html: string): string {
  const input = html ?? '';
  return sanitizeHtml(input, {
    // div は「動画のプレースホルダ」専用で通す。許可する属性は data-youtube-id だけで、
    // それ以外の div は transformTags でタグを解く（バックエンドの
    // App\Support\MemberPostHtml::ALLOWED_TAGS と必ず一致させる）
    allowedTags: ['p', 'br', 'strong', 'em', 'u', 's', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'blockquote', 'img', 'div'],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
      // height は許可しない。写真の大きさは width だけで決める方針で、両方が残ると
      // 縦横比の崩れた指定を表示してしまう（バックエンドの MemberPostHtml も落としている）
      img: ['src', 'alt', 'loading', 'width'],
      div: ['data-youtube-id'],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedSchemesByTag: {
      img: ['http', 'https'],
    },
    // プロトコル相対URL（"//evil.example.com/x" 等）の href を許さない。
    // バックエンド（MemberPostHtml::cleanAttributes）は相対パスでも http/https/mailto
    // でもない href を丸ごと削除するため、既定の true のままだと href が残ってしまい、
    // rel="noopener noreferrer" も付かない別オリジンリンクが漏れる
    allowProtocolRelative: false,
    transformTags: {
      a: (tagName, attribs) => {
        const href = attribs.href ?? '';
        const isExternal = /^https?:\/\//i.test(href);
        if (isExternal) {
          return { tagName, attribs: { ...attribs, target: '_blank', rel: 'noopener noreferrer' } };
        }
        return { tagName, attribs };
      },
      img: (tagName, attribs) => {
        if (!isAllowedProfileImageSrc(attribs.src ?? '')) {
          // src だけ落とすと <img> が残って壊れた画像アイコンが出るため、
          // 許可リストに無いタグ名へ変えてタグごと捨てさせる
          return { tagName: 'x-dropped-img', attribs: {} };
        }
        return { tagName, attribs };
      },
      div: transformBodyDiv,
    },
  });
}
